import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@mui/styles";

import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import Alert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import Snackbar from "@mui/material/Snackbar";
import InfoIcon from "@mui/icons-material/Info";
import Box from "@mui/material/Box";
import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const useStyles = makeStyles((theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      backgroundColor: '#262B31',
      backgroundImage: 'linear-gradient(180deg, rgba(255, 255, 255, 0.03) 0%, rgba(255, 255, 255, 0) 26%), radial-gradient(circle at top, rgba(167, 139, 250, 0.08) 0%, rgba(167, 139, 250, 0) 32%)',
      borderRadius: '20px',
      boxShadow: '0 28px 60px rgba(0, 0, 0, 0.42)',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      overflow: 'hidden',
    },
  },
  dialogTitle: {
    backgroundColor: 'rgba(18, 22, 27, 0.18)',
    color: '#ffffff',
    padding: '22px 24px 16px 24px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
  },
  dialogContent: {
    backgroundColor: 'transparent',
    padding: '24px',
    color: '#ffffff',
  },
  headerBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    borderRadius: '999px',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    marginBottom: '14px',
  },
  headerBadgeIcon: {
    color: '#A78BFA',
    fontSize: '0.95rem',
  },
  titleEyebrow: {
    color: 'rgba(255, 255, 255, 0.82)',
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    marginBottom: 0,
  },
  titleHeading: {
    color: '#ffffff',
    fontSize: '1.65rem',
    fontWeight: 700,
    lineHeight: 1.15,
    marginBottom: '10px',
  },
  titlePlayerName: {
    color: '#A78BFA',
    fontWeight: 700,
  },
  introText: {
    color: 'rgba(255, 255, 255, 0.8)',
    fontSize: '0.95rem',
    lineHeight: '1.5',
  },
  sectionBlock: {
    marginBottom: '14px',
  },
  sectionPanel: {
    backgroundColor: 'rgba(255, 255, 255, 0.035)',
    border: '1px solid rgba(255, 255, 255, 0.07)',
    borderRadius: '16px',
    padding: '16px',
    marginBottom: '16px',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.03)',
  },
  sectionTitle: {
    color: '#ffffff',
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: '6px',
  },
  sectionDescription: {
    color: 'rgba(255, 255, 255, 0.68)',
    fontSize: '0.88rem',
    lineHeight: 1.5,
  },
  offenseCategories: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "stretch",
    justifyContent: "flex-start",
    width: "100%",
    boxSizing: "border-box",
    gap: "10px",
  },
  offenseCategory: {
    margin: 0,
    minWidth: 'fit-content',
    borderRadius: '999px',
    padding: '5px 13px 5px 9px',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.02)',
    transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      borderColor: 'rgba(167, 139, 250, 0.32)',
      boxShadow: '0 8px 18px rgba(0, 0, 0, 0.18)',
    },
    '& .MuiFormControlLabel-label': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  offenseCategorySelected: {
    backgroundColor: 'rgba(232, 163, 61, 0.16)',
    borderColor: 'rgba(167, 139, 250, 0.52)',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.08), 0 10px 22px rgba(232, 163, 61, 0.12)',
  },
  offenseCategoryLabel: {
    fontSize: '0.9rem',
    color: '#ffffff',
    fontWeight: 600,
    lineHeight: 1.3,
  },
  offenseCategoryLabelDisabled: {
    fontSize: '0.9rem',
    color: 'rgba(255, 255, 255, 0.5)',
    fontWeight: 500,
    lineHeight: 1.3,
  },
  offenseCategoryDisabled: {
    margin: 0,
    opacity: 0.62,
    cursor: 'not-allowed',
    borderRadius: '999px',
    padding: '5px 13px 5px 9px',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.06)',
    '& .MuiFormControlLabel-label': {
      display: 'flex',
      alignItems: 'center',
    },
  },
  checkboxRoot: {
      padding: 0,
      marginRight: '0.55rem',
      '&:hover': {
        backgroundColor: 'transparent',
      },
  },
  customCheckboxIcon: {
      width: '1.3rem',
      height: '1.3rem',
      backgroundColor: '#393939',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out',
      border: '1px solid rgba(255, 255, 255, 0.12)',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.03)',
      [theme.breakpoints.up("xl")]: {
          width: '1.5rem',
          height: '1.5rem',
      },
  },
  customCheckboxIconChecked: {
      width: '1.3rem',
      height: '1.3rem',
      backgroundColor: '#E8A33D',
      borderRadius: '6px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'background-color 0.15s ease-in-out, border-color 0.15s ease-in-out',
      border: '1px solid #A78BFA',
      boxShadow: '0 6px 14px rgba(232, 163, 61, 0.18)',
      [theme.breakpoints.up("xl")]: {
          width: '1.5rem',
          height: '1.5rem',
      },
  },
  pickAllIcon: {
      color: "#ffffff",
      fontSize: "0.88rem",
      fontWeight: "bold",
      [theme.breakpoints.up("xl")]: {
          fontSize: '1rem',
      },
  },
  remarksLabel: {
    color: '#ffffff',
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: '6px',
  },
  remarksHint: {
    color: 'rgba(255, 255, 255, 0.68)',
    fontSize: '0.88rem',
    lineHeight: 1.5,
    marginBottom: '12px',
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'rgba(19, 23, 28, 0.52)',
      borderRadius: '14px',
      color: '#ffffff',
      fontSize: '0.95rem',
      boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.03)',
      '& fieldset': {
        borderColor: 'rgba(255, 255, 255, 0.08)',
        borderWidth: '1px',
        borderRadius: '14px',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(167, 139, 250, 0.34)',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#A78BFA',
        borderWidth: '2px',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: '0.9rem',
      '&.Mui-focused': {
        color: '#E8A33D',
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '16px 14px',
      '&::placeholder': {
        color: 'rgba(255, 255, 255, 0.5)',
      },
    },
  },
  dialogActions: {
    backgroundColor: 'rgba(18, 22, 27, 0.18)',
    padding: '16px 24px 22px 24px',
    borderTop: '1px solid rgba(255, 255, 255, 0.06)',
    gap: '12px',
    justifyContent: 'space-between',
  },
  actionsMeta: {
    color: 'rgba(255, 255, 255, 0.56)',
    fontSize: '0.78rem',
    lineHeight: 1.45,
    maxWidth: '240px',
  },
  actionsButtons: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    color: '#ffffff',
    borderRadius: '12px',
    padding: '10px 20px',
    fontWeight: '500',
    textTransform: 'none',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      borderColor: 'rgba(255, 255, 255, 0.12)',
    },
  },
  reportButton: {
    background: 'linear-gradient(180deg, #E8A33D 0%, #E8A33D 100%)',
    color: '#ffffff',
    borderRadius: '12px',
    padding: '10px 22px',
    fontWeight: '600',
    textTransform: 'none',
    boxShadow: '0 12px 24px rgba(232, 163, 61, 0.2)',
    '&:hover': {
      background: 'linear-gradient(180deg, #E8A33D 0%, #C4841D 100%)',
      boxShadow: '0 14px 28px rgba(232, 163, 61, 0.24)',
    },
    '&.Mui-disabled': {
      background: 'rgba(255, 255, 255, 0.08)',
      color: 'rgba(255, 255, 255, 0.38)',
    },
  },
  buttonLabel: {
    fontSize: '0.92rem',
    fontWeight: 'inherit',
    lineHeight: 1.2,
  },
  warningAlert: {
    marginBottom: '16px',
    backgroundColor: 'rgba(255, 152, 0, 0.1)',
    borderRadius: '14px',
    border: '1px solid rgba(255, 152, 0, 0.3)',
    '& .MuiAlert-icon': {
      color: '#ff9800',
    },
    '& .MuiAlert-message': {
      color: '#ffffff',
      fontSize: '0.85rem',
    },
  },
  tipsBox: {
    backgroundColor: 'rgba(52, 124, 255, 0.08)',
    border: '1px solid rgba(82, 154, 255, 0.26)',
    borderRadius: '16px',
    padding: '14px 16px',
    marginBottom: '18px',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.03)',
  },
  tipsIcon: {
    color: '#6AB4FF',
    marginTop: '2px',
    fontSize: '1.15rem',
  },
  tipsContent: {
    flex: 1,
  },
  tipsTitle: {
    color: '#8EC7FF',
    fontSize: '0.92rem',
    fontWeight: 700,
    marginBottom: '6px',
  },
  tipsList: {
    margin: 0,
    paddingLeft: '18px',
  },
  tipsListItem: {
    display: 'list-item',
    padding: 0,
    marginBottom: '4px',
  },
  tipsListText: {
    color: 'rgba(255, 255, 255, 0.82)',
    fontSize: '0.82rem',
    lineHeight: 1.6,
  },
  successSnackbar: {
    '& .MuiSnackbarContent-root': {
      backgroundColor: '#4caf50',
      color: '#ffffff',
      fontSize: '0.95rem',
      fontWeight: 500,
      borderRadius: '12px',
    },
  },
}));

const reportTypes = [
  { name: "cheating", label: "Cheating" },
  { name: "griefing", label: "Griefing" },
  { name: "smurfing", label: "Smurfing" },
  { name: "abusive_comms", label: "Abusive Communication" },
  { name: "not_team_player", label: "Not a team player" },
  { name: "player_not_vocal", label: "Non communicative player" }
];

export default function ReportDialog({ openReportDialog, onClose, steamId, nickname, matchId, actions, onSubmitted }) {
  const classes = useStyles();
  const [selectedOffenses, setSelectedOffenses] = useState({});
  const [remarks, setRemarks] = useState("");
  const [alreadyReportedOffenses, setAlreadyReportedOffenses] = useState([]);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [duplicateAttemptWarning, setDuplicateAttemptWarning] = useState(null);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);

  // Fetch existing reported offenses when dialog opens
  useEffect(() => {
    if (openReportDialog && steamId && matchId) {
      setLoadingExisting(true);
      fetch(`${basePath}/api/v1/match/report-player/existing?matchId=${matchId}&reportedSteamId=${steamId}`, {
        credentials: 'include'
      })
        .then(res => res.json())
        .then(data => {
          if (data.ok && data.reportedOffenses) {
            setAlreadyReportedOffenses(data.reportedOffenses);
          }
        })
        .catch(err => {
          console.error("Failed to fetch existing reports:", err);
        })
        .finally(() => {
          setLoadingExisting(false);
        });
    }
  }, [openReportDialog, steamId, matchId]);

  const handleCheckboxChange = (name) => {
    // Check if this offense was already reported
    if (alreadyReportedOffenses.includes(name)) {
      const reportType = reportTypes.find(rt => rt.name === name);
      setDuplicateAttemptWarning(`You have already reported this player for "${reportType?.label || name}" in this match.`);
      
      // Clear warning after 4 seconds
      setTimeout(() => {
        setDuplicateAttemptWarning(null);
      }, 4000);
      
      return;
    }

    const updatedOffenses = {
      ...selectedOffenses,
      [name]: !selectedOffenses[name]
    };
    setSelectedOffenses(updatedOffenses);
    
    if (actions && actions.updateReportPlayerState) {
      actions.updateReportPlayerState({
        offenses: updatedOffenses,
        remarks: remarks
      });
    }
  };

  const handleRemarksChange = (event) => {
    const newRemarks = event.target.value;
    setRemarks(newRemarks);
    
    if (actions && actions.updateReportPlayerState) {
      actions.updateReportPlayerState({
        offenses: selectedOffenses,
        remarks: newRemarks
      });
    }
  };

  const handleReport = () => {
    if (actions && actions.submitPlayerReport) {
      const maybePromise = actions.submitPlayerReport({
        reportedSteamId: steamId,
        offenses: selectedOffenses,
        remarks: remarks,
        matchId: matchId
      });
      
      if (maybePromise && typeof maybePromise.then === "function") {
        maybePromise.then((result) => {
          if (result.ok) {
            // Only reset state and close dialog on success
            setSelectedOffenses({});
            setRemarks("");
            setAlreadyReportedOffenses([]);
            setDuplicateAttemptWarning(null);
            setShowSuccessSnackbar(true);
            onClose();
          } else if (result.alreadyReported && result.duplicateOffenses) {
            // Show error for duplicate offenses
            const offenseLabels = result.duplicateOffenses
              .map(offense => reportTypes.find(rt => rt.name === offense)?.label || offense)
              .join(", ");
            setDuplicateAttemptWarning(`You have already reported this player for: ${offenseLabels}`);
            
            // Update the already reported list
            setAlreadyReportedOffenses(prev => [
              ...new Set([...prev, ...result.duplicateOffenses])
            ]);
          } else {
            // Other error
            setDuplicateAttemptWarning(result.message || "Failed to submit report");
          }
          
          if (onSubmitted) onSubmitted(result);
        });
      } else {
        if (onSubmitted) onSubmitted({ ok: false, message: "Report did not return a result" });
      }
    }
  };

  const handleClose = () => {
    setSelectedOffenses({});
    setRemarks("");
    setAlreadyReportedOffenses([]);
    setDuplicateAttemptWarning(null);
    
    if (actions && actions.clearReportPlayerState) {
      actions.clearReportPlayerState();
    }
    
    onClose();
  };

  const isSubmitDisabled = !Object.values(selectedOffenses).some(v => v === true) || loadingExisting;
  const reportingTips = [
    'Mention specific round numbers where the incident occurred.',
    'Describe what happened as clearly as you can.',
    'For cheating or smurfing, include the pattern you noticed.',
    'Add any context that would help moderators review the case.'
  ];

  return (
    <div>
      <Dialog
        open={openReportDialog}
        onClose={handleClose}
        aria-labelledby="report-player-dialog-title"
        aria-describedby="report-player-dialog-description"
        className={classes.dialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className={classes.dialogTitle}>
          <Box className={classes.headerBadge}>
            <GppGoodOutlinedIcon className={classes.headerBadgeIcon} />
            <Typography className={classes.titleEyebrow}>
              Fair play review
            </Typography>
          </Box>
          <Typography id="report-player-dialog-title" className={classes.titleHeading}>
            Help us review {` `}
            <Box component="span" className={classes.titlePlayerName}>
              {nickname}
            </Box>
          </Typography>
          <Typography id="report-player-dialog-description" className={classes.introText}>
            Share what happened in a few clear details. The more specific you are, the easier it is for moderators to review fairly.
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Box className={classes.tipsBox}>
            <InfoIcon className={classes.tipsIcon} />
            <Box className={classes.tipsContent}>
              <Typography className={classes.tipsTitle}>
                A strong report usually includes:
              </Typography>
              <Box component="ul" className={classes.tipsList}>
                {reportingTips.map((tip) => (
                  <Box key={tip} component="li" className={classes.tipsListItem}>
                    <Typography className={classes.tipsListText}>
                      {tip}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          {duplicateAttemptWarning && (
            <Alert severity="warning" className={classes.warningAlert}>
              <Typography variant="body2">
                {duplicateAttemptWarning}
              </Typography>
            </Alert>
          )}

          {alreadyReportedOffenses.length > 0 && !duplicateAttemptWarning && (
            <Alert severity="info" className={classes.warningAlert}>
              <Typography variant="body2">
                You have already reported this player for some offenses in this match. Those options are disabled.
              </Typography>
            </Alert>
          )}

          <Box className={classes.sectionPanel}>
            <Box className={classes.sectionBlock}>
              <Typography className={classes.sectionTitle}>
                What best describes the issue?
              </Typography>
              <Typography className={classes.sectionDescription}>
                Choose every option that applies so the review starts with the right context.
              </Typography>
            </Box>

            <div className={classes.offenseCategories}>
              {reportTypes.map((reportType) => {
                const isAlreadyReported = alreadyReportedOffenses.includes(reportType.name);
                const isSelected = Boolean(selectedOffenses[reportType.name]);
                const labelClassName = isAlreadyReported
                  ? classes.offenseCategoryLabelDisabled
                  : classes.offenseCategoryLabel;
                const checkboxElement = (
                  <FormControlLabel
                    key={reportType.name}
                    control={
                      <Checkbox
                          name={reportType.name}
                          checked={isAlreadyReported || selectedOffenses[reportType.name] || false}
                          onChange={() => handleCheckboxChange(reportType.name)}
                          classes={{ root: classes.checkboxRoot }}
                          icon={<span className={classes.customCheckboxIcon} />}
                          checkedIcon={
                              <span className={classes.customCheckboxIconChecked}>
                                  <CheckIcon className={classes.pickAllIcon}/>
                              </span>
                          }
                          disabled={isAlreadyReported}
                          disableRipple
                      />
                    }
                    className={isAlreadyReported
                      ? classes.offenseCategoryDisabled
                      : `${classes.offenseCategory} ${isSelected ? classes.offenseCategorySelected : ''}`.trim()}
                    label={(
                      <Typography className={labelClassName}>
                        {reportType.label}
                      </Typography>
                    )}
                  />
                );

                return isAlreadyReported ? (
                  <Tooltip
                    key={reportType.name}
                    title={`You already reported this player for ${reportType.label.toLowerCase()}`}
                    arrow
                    placement="top"
                  >
                    <span>{checkboxElement}</span>
                  </Tooltip>
                ) : checkboxElement;
              })}
            </div>
          </Box>

          <Box className={classes.sectionPanel}>
            <Box className={classes.sectionBlock}>
              <Typography className={classes.remarksLabel}>
                Anything else you want the team to know?
              </Typography>
              <Typography className={classes.remarksHint}>
                Optional, but very helpful when you can include short examples, round numbers, or a quick timeline.
              </Typography>
            </Box>

            <TextField
              id="player-report-remarks"
              className={classes.textField}
              fullWidth
              multiline
              rows={4}
              value={remarks}
              onChange={handleRemarksChange}
              variant="outlined"
              placeholder='Example: "Round 7 - team-killed me at spawn. Round 12 - blocked the doorway and refused to move."'
            />
          </Box>
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Typography className={classes.actionsMeta}>
            Reports are reviewed manually, with match context and prior submissions taken into account.
          </Typography>
          <Box className={classes.actionsButtons}>
            <Button
              onClick={handleClose}
              className={classes.cancelButton}
              variant="contained"
            >
              <Typography className={classes.buttonLabel}>
                Cancel
              </Typography>
            </Button>
            <Button
              className={classes.reportButton}
              autoFocus
              variant="contained"
              onClick={handleReport}
              disabled={isSubmitDisabled}
            >
              <Typography className={classes.buttonLabel}>
                Submit report
              </Typography>
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={4000}
        onClose={() => setShowSuccessSnackbar(false)}
        message={(
          <Typography variant="body2">
            ✓ Report submitted successfully! Our moderators will review it.
          </Typography>
        )}
        className={classes.successSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </div>
  );
}

ReportDialog.propTypes = {
  openReportDialog: PropTypes.bool,
  onClose: PropTypes.func,
  steamId: PropTypes.string,
  nickname: PropTypes.string,
  matchId: PropTypes.string,
  actions: PropTypes.object,
  onSubmitted: PropTypes.func,
};
